import { useState } from "react";
import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  MessageBar,
  MessageBarBody,
  ProgressBar,
  Spinner,
  Text,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import {
  ArrowLeft20Regular,
  Delete20Regular,
  Play20Regular,
  ArrowClockwise20Regular,
} from "@fluentui/react-icons";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  useCancelImageFileMigration,
  useImageFileMigration,
  useStartImageFileMigration,
} from "../hooks/useImageFileMigration";
import { ClientRoutes } from "../models/ClientRoutes";
import {
  isPublishedMediaMigrationActive,
  PublishedMediaMetadataMigrationStatus,
} from "../models/PublishedMediaMetadataMigration";
import { VsaaColorTokens } from "../style/VsaaColorTokens";

const storageKey = "image-file-migration-job-id";
const statusKeys: Record<PublishedMediaMetadataMigrationStatus, string> = {
  0: "pending",
  1: "running",
  2: "completed",
  3: "failed",
  4: "cancelled",
  5: "completedWithExceptions",
  6: "cancelling",
};
const counterKeys = [
  "scanned",
  "updated",
  "skipped",
  "fileMissing",
  "ambiguous",
  "exceptions",
  "conflicts",
] as const;
type Action = "start" | "resume" | "retry" | "cancel";

const useStyles = makeStyles({
  root: {
    padding: tokens.spacingVerticalL,
    width: "100%",
    boxSizing: "border-box",
    minWidth: 0,
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: tokens.spacingHorizontalM,
  },
  actions: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: tokens.spacingHorizontalS,
  },
  panel: {
    marginTop: tokens.spacingVerticalL,
    paddingTop: tokens.spacingVerticalL,
    borderTop: `1px solid ${VsaaColorTokens.color_file_extension_badge_border}`,
  },
  rows: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: tokens.spacingHorizontalL,
  },
  item: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
    minWidth: 0,
    overflowWrap: "anywhere",
  },
  progress: { marginTop: tokens.spacingVerticalL },
  report: { overflowX: "auto", marginTop: tokens.spacingVerticalL },
  table: { width: "100%", textAlign: "left", overflowWrap: "anywhere" },
  danger: {
    backgroundColor: VsaaColorTokens.color_destructive_button_background,
    color: VsaaColorTokens.color_destructive_button_text,
    ":hover": {
      backgroundColor:
        VsaaColorTokens.color_destructive_button_background_hover,
    },
  },
});

export default function ImageFileMigrationPage() {
  const classes = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [jobId, setJobId] = useState(
    () => localStorage.getItem(storageKey) ?? undefined
  );
  const [dryRun, setDryRun] = useState(true);
  const [confirmAction, setConfirmAction] = useState<Action | null>(null);
  const [actionError, setActionError] = useState<"start" | "cancel">();
  const statusQuery = useImageFileMigration(jobId);
  const startMutation = useStartImageFileMigration();
  const cancelMutation = useCancelImageFileMigration(jobId);
  const missingJob = Boolean(jobId && statusQuery.error?.status === 404);
  const job =
    !missingJob && statusQuery.data?.id === jobId
      ? statusQuery.data
      : undefined;
  const active = isPublishedMediaMigrationActive(job?.status);
  const pending = startMutation.isPending || cancelMutation.isPending;
  const mode =
    confirmAction === "start"
      ? dryRun
      : (job?.imageFileMigrationDryRun ?? true);

  const start = async (action: Exclude<Action, "cancel">) => {
    setActionError(undefined);
    try {
      const started = await startMutation.mutateAsync({
        dryRun: action === "start" ? dryRun : job!.imageFileMigrationDryRun,
        ...(action !== "start" && { resumeJobId: jobId }),
        ...(action === "retry" && { retryExceptions: true }),
      });
      localStorage.setItem(storageKey, started.id);
      setJobId(started.id);
    } catch {
      setActionError("start");
    } finally {
      setConfirmAction(null);
    }
  };

  const cancel = async () => {
    setActionError(undefined);
    try {
      await cancelMutation.mutateAsync();
    } catch {
      setActionError("cancel");
    } finally {
      setConfirmAction(null);
    }
  };

  const forgetJob = () => {
    localStorage.removeItem(storageKey);
    setJobId(undefined);
  };

  return (
    <main className={classes.root} aria-live="polite">
      <div className={classes.header}>
        <Button
          appearance="subtle"
          icon={<ArrowLeft20Regular />}
          onClick={() => navigate(ClientRoutes.SystemAdmin)}
        >
          {t("imageFileMigration.back")}
        </Button>
        <Text as="h1" size={600} weight="semibold">
          {t("imageFileMigration.title")}
        </Text>
        <div className={classes.actions}>
          <Checkbox
            label={t("imageFileMigration.dryRun")}
            checked={dryRun}
            disabled={active || pending}
            onChange={(_, data) => setDryRun(data.checked === true)}
          />
          {!active && (
            <Button
              appearance="primary"
              icon={<Play20Regular />}
              disabled={
                pending || Boolean(jobId && (!job || statusQuery.error))
              }
              onClick={() => setConfirmAction("start")}
            >
              {t("imageFileMigration.start")}
            </Button>
          )}
          {active && (
            <Button
              className={classes.danger}
              icon={<Delete20Regular />}
              disabled={pending}
              onClick={() => setConfirmAction("cancel")}
            >
              {t("imageFileMigration.cancel")}
            </Button>
          )}
          {!active && !statusQuery.error && job?.canResume && (
            <Button
              icon={<ArrowClockwise20Regular />}
              disabled={pending}
              onClick={() => setConfirmAction("resume")}
            >
              {t("imageFileMigration.resume")}
            </Button>
          )}
          {!active && !statusQuery.error && job?.canRetryExceptions && (
            <Button
              icon={<ArrowClockwise20Regular />}
              disabled={pending}
              onClick={() => setConfirmAction("retry")}
            >
              {t("imageFileMigration.retry")}
            </Button>
          )}
        </div>
      </div>

      {!navigator.onLine && (
        <MessageBar intent="warning">
          <MessageBarBody>{t("imageFileMigration.offline")}</MessageBarBody>
        </MessageBar>
      )}
      {missingJob && (
        <MessageBar intent="warning">
          <MessageBarBody>{t("imageFileMigration.missingJob")}</MessageBarBody>
          <Button onClick={forgetJob}>
            {t("imageFileMigration.forgetJob")}
          </Button>
        </MessageBar>
      )}
      {statusQuery.error && !missingJob && (
        <MessageBar intent="error">
          <MessageBarBody>{t("imageFileMigration.loadError")}</MessageBarBody>
        </MessageBar>
      )}
      {actionError && (
        <div role="alert">
          <MessageBar intent="error">
            <MessageBarBody>
              {t(`imageFileMigration.${actionError}Error`)}
            </MessageBarBody>
          </MessageBar>
        </div>
      )}
      {!job && statusQuery.isLoading && (
        <div className={classes.panel}>
          <Spinner label={t("imageFileMigration.loading")} />
        </div>
      )}
      {!job && !statusQuery.isLoading && !statusQuery.error && (
        <div className={classes.panel}>
          <Text>{t("imageFileMigration.empty")}</Text>
        </div>
      )}
      {job && (
        <section className={classes.panel}>
          <div className={classes.rows}>
            <div className={classes.item}>
              <Text>{t("imageFileMigration.status")}</Text>
              <Badge>
                {t(`imageFileMigration.states.${statusKeys[job.status]}`)}
              </Badge>
            </div>
            <div className={classes.item}>
              <Text>{t("imageFileMigration.mode")}</Text>
              <Text>
                {t(
                  job.imageFileMigrationDryRun
                    ? "imageFileMigration.dryRunMode"
                    : "imageFileMigration.liveMode"
                )}
              </Text>
            </div>
            <div className={classes.item}>
              <Text>{t("imageFileMigration.jobId")}</Text>
              <Text>{job.id}</Text>
            </div>
            <div className={classes.item}>
              <Text>{t("imageFileMigration.started")}</Text>
              <Text>{new Date(job.startTime).toLocaleString("sv-SE")}</Text>
            </div>
            <div className={classes.item}>
              <Text>{t("imageFileMigration.heartbeat")}</Text>
              <Text>
                {job.heartbeatAtUtc
                  ? new Date(job.heartbeatAtUtc).toLocaleString("sv-SE")
                  : "-"}
              </Text>
            </div>
            <div className={classes.item}>
              <Text>{t("imageFileMigration.page")}</Text>
              <Text>{job.pageNumber}</Text>
            </div>
            {counterKeys.map((counter) => (
              <div className={classes.item} key={counter}>
                <Text>{t(`imageFileMigration.${counter}`)}</Text>
                <Text>{job.mediaMigrationCounters?.[counter] ?? 0}</Text>
              </div>
            ))}
            {!active && job.endTime && (
              <div className={classes.item}>
                <Text>{t("imageFileMigration.finished")}</Text>
                <Text>{new Date(job.endTime).toLocaleString("sv-SE")}</Text>
              </div>
            )}
          </div>
          <ProgressBar
            className={classes.progress}
            value={active ? undefined : 1}
            aria-label={t("imageFileMigration.progress")}
          />
          {job.status === PublishedMediaMetadataMigrationStatus.Failed && (
            <MessageBar intent="error">
              <MessageBarBody>
                {t("imageFileMigration.failedMessage")}
              </MessageBarBody>
            </MessageBar>
          )}
          {job.mediaMigrationExceptions?.length > 0 && (
            <div className={classes.report}>
              <Text as="h2" size={500} weight="semibold">
                {t("imageFileMigration.report")}
              </Text>
              <table className={classes.table}>
                <thead>
                  <tr>
                    <th scope="col">{t("imageFileMigration.versionId")}</th>
                    <th scope="col">{t("imageFileMigration.errorCode")}</th>
                    <th scope="col">{t("imageFileMigration.message")}</th>
                    <th scope="col">{t("imageFileMigration.occurred")}</th>
                  </tr>
                </thead>
                <tbody>
                  {job.mediaMigrationExceptions.map((exception, index) => (
                    <tr key={`${exception.versionId}-${index}`}>
                      <td>{exception.versionId}</td>
                      <td>{exception.code}</td>
                      <td>{exception.message}</td>
                      <td>
                        {new Date(exception.occurredAtUtc).toLocaleString(
                          "sv-SE"
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      <Dialog
        open={confirmAction !== null}
        onOpenChange={(_, data) => !data.open && setConfirmAction(null)}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>
              {t(
                `imageFileMigration.confirm.${confirmAction ?? "start"}.title`
              )}
            </DialogTitle>
            <DialogContent>
              {t(`imageFileMigration.confirm.${confirmAction ?? "start"}.body`)}
              {confirmAction !== "cancel" && (
                <p>
                  {t(
                    mode
                      ? "imageFileMigration.confirm.dryRun"
                      : "imageFileMigration.confirm.live"
                  )}
                </p>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirmAction(null)}>
                {t("imageFileMigration.close")}
              </Button>
              <Button
                appearance="primary"
                className={
                  confirmAction === "cancel" ? classes.danger : undefined
                }
                disabled={pending}
                onClick={() =>
                  confirmAction === "cancel"
                    ? cancel()
                    : confirmAction && start(confirmAction)
                }
              >
                {t(
                  !mode && confirmAction !== "cancel"
                    ? "imageFileMigration.confirmLive"
                    : "imageFileMigration.confirmAction"
                )}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </main>
  );
}
